import { inject, Injectable } from '@angular/core';
import { FormService } from '../form.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { isEqual, isNumber } from 'lodash-es';
import {
  checkCurrentFormForPendingChanges,
  createForm,
  deleteForm,
  dispatchedCreateUpdateForm,
  duplicateForm,
  formUpdateQuestion,
  getFormInPrevVersion,
  getFormIntegrations,
  getFormRules,
  loadFormList,
  loadFormListByDatasetId,
  loadFormListByStatuses,
  loadFormListWithStudies,
  loadProjectContextFormList,
  pendingCreateUpdateForm,
  populateCurrentForm,
  populateCurrentFormSnapshot,
  populateFormInPrevVersion,
  populateFormIntegrations,
  populateFormRules,
  refreshCurrentForm,
  releaseForm,
  removeForm,
  searchFormById,
  successCreateUpdateForm,
  updateForm,
  updateFormField,
  updateFormInList,
  updateFormList,
  updateReleasedForm,
} from './form.actions';

import { catchError, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { FormModel, FormTypeEnum } from '../form.model';
import { addLoader, errorPopup, globalLoading, removeLoader, successPopup } from '../../store/actions/ui.actions';
import { Store } from '@ngrx/store';
import { AppState, getState } from '../../store/models/app.state';
import { FormRenderingMode } from '../_form-settings/form-rendering-mode.enum';
import { QuestionService } from '../../question/question.service';
import { Statuses } from '../../shared/models/statuses.enum';
import { selectCurrentFormState } from './form.state';
import { UtilsService } from '../../core/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class FormEffects {
  store = inject(Store<AppState>);
  actions = inject(Actions);
  formService = inject(FormService);
  questionService = inject(QuestionService);
  utilsService = inject(UtilsService);
  translateService = inject(TranslateService);
  loadFormsList = createEffect(() => {
    return this.actions.pipe(
      ofType(loadFormList),
      mergeMap(() =>
        this.formService.getFormList().pipe(
          mergeMap((res: FormModel[]) => [
            updateFormList({
              formList: res || [],
            }),
            globalLoading(false),
          ]),
          catchError(() => {
            return [globalLoading(false), errorPopup({ error: 'FORM.MESSAGES.UNABLE_TO_GET_FORM_LIST' })];
          }),
        ),
      ),
    );
  });
  loadFormsListByStatuses = createEffect(() => {
    return this.actions.pipe(
      ofType(loadFormListByStatuses),
      mergeMap(({ statuses }) =>
        this.formService.getFormList(statuses).pipe(
          mergeMap((res: FormModel[]) => [
            updateFormList({
              formList: res || [],
            }),
            globalLoading(false),
          ]),
          catchError(() => {
            return [globalLoading(false), errorPopup({ error: 'FORM.MESSAGES.UNABLE_TO_GET_FORM_LIST' })];
          }),
        ),
      ),
    );
  });
  loadFormsListWithStudies = createEffect(() => {
    return this.actions.pipe(
      ofType(loadFormListWithStudies),
      mergeMap(() =>
        this.formService.getFormListWithStudies().pipe(
          mergeMap((res: FormModel[]) => [
            updateFormList({
              formList: res || [],
            }),
            globalLoading(false),
          ]),
          catchError(() => {
            return [globalLoading(false), errorPopup({ error: 'FORM.MESSAGES.UNABLE_TO_GET_FORM_LIST' })];
          }),
        ),
      ),
    );
  });
  loadFormsListInProjectContext = createEffect(() => {
    return this.actions.pipe(
      ofType(loadProjectContextFormList),
      mergeMap(({ ids }) =>
        this.formService.getFormListInProjectContext(ids).pipe(
          mergeMap((res: FormModel[]) => [
            updateFormList({
              formList: res || [],
            }),
            globalLoading(false),
          ]),
          catchError(() => {
            return [globalLoading(false), errorPopup({ error: 'FORM.MESSAGES.UNABLE_TO_GET_FORM_LIST' })];
          }),
        ),
      ),
    );
  });
  loadFormsByDatasetId = createEffect(() => {
    return this.actions.pipe(
      ofType(loadFormListByDatasetId),
      mergeMap(({ id }) =>
        this.formService.getFormListByDatasetId(id).pipe(
          mergeMap((res: FormModel[]) => [
            updateFormList({
              formList: res || [],
            }),
            globalLoading(false),
          ]),
          catchError(() => {
            return [globalLoading(false)];
          }),
        ),
      ),
    );
  });
  searchFormById = createEffect(() => {
    return this.actions.pipe(
      ofType(searchFormById),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action =>
        this.formService.searchById(action.id).pipe(
          //TODO: temporary display correct version
          map(form =>
            this.normalizeForm({
              ...form,
              version: String(form.versionId),
            }),
          ),
          mergeMap((res: FormModel) => {
            if (!Object.prototype.hasOwnProperty.call(res.settings, 'renderingMode')) {
              res.settings['renderingMode'] = FormRenderingMode.PAGE_BY_PAGE;
            } // handle the old forms that doesn't have rendering mode yet

            return [
              populateCurrentForm({ form: res }),
              populateCurrentFormSnapshot({ form: res }),
              getFormInPrevVersion({ form: res }),
              removeLoader(action),
              globalLoading(false),
            ];
          }),
          catchError(() => {
            return [
              removeLoader(action),
              globalLoading(false),
              errorPopup({ error: 'FORM.MESSAGES.UNABLE_TO_FIND_FORM' }),
            ];
          }),
        ),
      ),
    );
  });

  getFormInPrevForm = createEffect(() => {
    return this.actions.pipe(
      ofType(getFormInPrevVersion),
      filter(({ form }) => form.isAmended),
      mergeMap(({ form }) =>
        this.formService.getFormQuestionsPreviousVersion(form.id, true).pipe(
          mergeMap((res: FormModel) => {
            return [populateFormInPrevVersion({ form: res })];
          }),
          catchError(() => {
            return [];
          }),
        ),
      ),
    );
  });
  createForm = createEffect(() => {
    return this.actions.pipe(
      ofType(createForm),
      mergeMap(({ form }) =>
        this.formService.create(form).pipe(
          mergeMap((res: FormModel) => [
            populateCurrentForm({ form: this.normalizeForm(res) }),
            populateCurrentFormSnapshot({ form: this.normalizeForm(res) }),
            pendingCreateUpdateForm({ pending: false }),
            successCreateUpdateForm({ success: true }),
            successPopup({ message: `FORM.MESSAGES.${this.getRecordType(res).toUpperCase()}_CREATED` }),
            globalLoading(false),
          ]),
          catchError((e: HttpErrorResponse) => {
            const error = this.utilsService.extractErrorMessage(
              e.error.error,
              this.translateService.instant(`FORM.MESSAGES.UNABLE_TO_CREATE_FORM`),
            );
            return [globalLoading(false), errorPopup({ error })];
          }),
        ),
      ),
    );
  });
  updateForm = createEffect(() => {
    return this.actions.pipe(
      ofType(updateForm),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action => {
        return this.formService.update(action.form).pipe(
          mergeMap((res: FormModel) => {
            return [
              updateFormInList({ form: this.normalizeForm(res) }),
              populateCurrentForm({ form: this.normalizeForm(res) }),
              populateCurrentFormSnapshot({ form: this.normalizeForm(res) }),
              successCreateUpdateForm({ success: true }),
              pendingCreateUpdateForm({ pending: false }),
              dispatchedCreateUpdateForm({ dispatched: false }),
              successPopup({ message: `FORM.MESSAGES.${this.getRecordType(res).toUpperCase()}_UPDATED` }),
              removeLoader(action),
              globalLoading(false),
              successCreateUpdateForm({ success: false }),
            ];
          }),
          catchError(e => {
            const error = this.utilsService.extractErrorMessage(
              e.error.error,
              this.translateService.instant(`FORM.MESSAGES.UNABLE_TO_UPDATE_FORM`),
            );
            return [
              removeLoader(action),
              errorPopup({ error }),
              successCreateUpdateForm({ success: false }),
              pendingCreateUpdateForm({ pending: false }),
              dispatchedCreateUpdateForm({ dispatched: false }),
              globalLoading(false),
            ];
          }),
        );
      }),
    );
  });
  deleteForm = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteForm),
      mergeMap(({ id }) =>
        this.formService.deleteForm(id).pipe(
          mergeMap(() => [removeForm({ id }), successPopup({ message: 'FORM.MESSAGES.FORM_DELETED' })]),
          catchError(() => {
            return [errorPopup({ error: 'FORM.MESSAGES.UNABLE_TO_DELETE_FORM' }), globalLoading(false)];
          }),
        ),
      ),
    );
  });

  duplicateForm = createEffect(() => {
    return this.actions.pipe(
      ofType(duplicateForm),
      mergeMap(({ id }) =>
        this.formService.duplicateForm(id).pipe(
          map(form => {
            if (form.formStatus === Statuses.Released) {
              return [Statuses.Released];
            }

            return [Statuses.Draft, Statuses.PendingReview, Statuses.UnderReview, Statuses.Rejected];
          }),
          mergeMap(statuses => [
            loadFormListByStatuses({ statuses }),
            successPopup({ message: 'FORM.MESSAGES.FORM_DUPLICATED' }),
          ]),
          catchError(() => {
            return [errorPopup({ error: 'FORM.MESSAGES.UNABLE_TO_DUPLICATE_FORM' })];
          }),
        ),
      ),
    );
  });
  updateFormField = createEffect(() => {
    return this.actions.pipe(
      ofType(updateFormField, successCreateUpdateForm, populateCurrentFormSnapshot, formUpdateQuestion),
      withLatestFrom(this.store.select(selectCurrentFormState)),
      mergeMap(([, { form, formSnapshot }]) => {
        return [
          checkCurrentFormForPendingChanges({
            changes: !isEqual(form, formSnapshot),
          }),
        ];
      }),
      catchError(() => []),
    );
  });

  populateCurrentForm = createEffect(() => {
    return this.actions.pipe(
      ofType(populateCurrentForm),
      mergeMap(({ form }) => [getFormRules({ formId: form.id }), getFormIntegrations({ formId: form.id })]),
      catchError(() => {
        return [];
      }),
    );
  });

  getFormRules = createEffect(() => {
    return this.actions.pipe(
      ofType(getFormRules),
      mergeMap(({ formId }) =>
        this.formService.getIntegrationRulesByFormId(formId).pipe(
          map(rules =>
            rules.map(r => {
              const numeric = Number(r.additionalValue);
              return {
                ...r,
                additionalValue: r.additionalValue && isNumber(numeric) ? numeric : r.additionalValue,
              };
            }),
          ),
          mergeMap(formRules => [populateFormRules({ formRules })]),
          catchError(() => {
            return [];
          }),
        ),
      ),
    );
  });

  getFormIntegrations = createEffect(() => {
    return this.actions.pipe(
      ofType(getFormIntegrations),
      mergeMap(({ formId }) =>
        this.formService.getIntegrationsByFormId(formId).pipe(
          mergeMap(integrations => [populateFormIntegrations({ integrations })]),
          catchError(() => {
            return [];
          }),
        ),
      ),
    );
  });

  refreshCurrentForm = createEffect(() => {
    return this.actions.pipe(
      ofType(refreshCurrentForm),
      mergeMap(() => {
        const formId = getState(this.store).form.current.form.id;
        return [searchFormById({ id: formId })];
      }),
      catchError(() => {
        return [];
      }),
    );
  });

  releaseForm = createEffect(() => {
    return this.actions.pipe(
      ofType(releaseForm),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action => {
        return this.formService.releaseForm(action.formId).pipe(
          mergeMap(() => {
            return [
              successCreateUpdateForm({ success: true }),
              pendingCreateUpdateForm({ pending: false }),
              dispatchedCreateUpdateForm({ dispatched: false }),
              successCreateUpdateForm({ success: false }),
              searchFormById({ id: action.formId, addLoader: action.addLoader }),
              removeLoader(action),
              successPopup({ message: 'FORM.MESSAGES.FORM_RELEASED' }),
            ];
          }),
          catchError(e => {
            const errorMSg = this.utilsService.extractErrorMessage(
              e.error?.error || '',
              this.translateService.instant('FORM.MESSAGES.UNABLE_TO_RELEASE'),
            );
            return [
              errorPopup({ error: errorMSg }),
              removeLoader(action),
              globalLoading(false),
              successCreateUpdateForm({ success: false }),
              pendingCreateUpdateForm({ pending: false }),
              dispatchedCreateUpdateForm({ dispatched: false }),
            ];
          }),
        );
      }),
    );
  });
  updateReleasedForm = createEffect(() => {
    return this.actions.pipe(
      ofType(updateReleasedForm),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action => {
        return this.formService.updateReleasedForm(action.formId).pipe(
          mergeMap(() => {
            return [
              successCreateUpdateForm({ success: true }),
              pendingCreateUpdateForm({ pending: false }),
              dispatchedCreateUpdateForm({ dispatched: false }),
              successCreateUpdateForm({ success: false }),
              searchFormById({ id: action.formId, addLoader: action.addLoader }),
              removeLoader(action),
              successPopup({ message: 'FORM.MESSAGES.FORM_RELEASED' }),
            ];
          }),
          catchError(e => {
            const errorMSg = this.utilsService.extractErrorMessage(
              e.error?.error || '',
              this.translateService.instant('FORM.MESSAGES.UNABLE_TO_RELEASE'),
            );
            return [
              errorPopup({ error: errorMSg }),
              globalLoading(false),
              removeLoader(action),
              successCreateUpdateForm({ success: false }),
              pendingCreateUpdateForm({ pending: false }),
              dispatchedCreateUpdateForm({ dispatched: false }),
            ];
          }),
        );
      }),
    );
  });

  getRecordType(form: FormModel): string {
    switch (form.type) {
      case FormTypeEnum.Report:
        return 'Report';
      default:
        return 'Form';
    }
  }

  normalizeForm(form: FormModel): FormModel {
    return { ...form, code: form.formShort.code };
  }
}
